/**
 * MST-1.90 — the milestone catalogue itself, lifted out of `MilestonesTab` so it
 * can be shown somewhere other than the Pipelines tab.
 *
 * ⚠️ THE CATALOGUE MOVED, THE MAPPING DID NOT, AND EVERY SURFACE THAT SHOWS THIS
 * HAS TO SAY SO. Creating a milestone here changes nothing on its own: a project
 * reaches a milestone by entering a STAGE mapped to it, and stages are mapped in
 * Settings → Pipelines → Manage Stages. A panel that let somebody add three
 * milestones and walk away believing commission could now release would be the
 * exact false reassurance `commissionAccountReadiness.ts` exists to remove.
 * `MILESTONE_SETUP_COPY.secondStep` is that sentence, said on every host.
 *
 * ⚠️ NO CARD, NO PANEL, NO HEADING. This renders body content only, so the host
 * decides the frame. `MilestonesTab` wraps it in a Card; `MilestoneSetupPanel`
 * wraps it in a SlidePanel over the Commission tab.
 */
import { useEffect, useState } from 'react';
import { Archive, Pencil, Plus, RotateCcw } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useToast } from '@/components/ui/use-toast';
import { useTenant } from '@/contexts/TenantContext';
import {
  createMilestone,
  fetchMilestones,
  fetchStageCountsByMilestone,
  retireMilestone,
  updateMilestone,
  type Milestone,
} from '@/services/milestoneService';

export interface MilestoneCatalogueProps {
  /**
   * Fired after any successful write.
   *
   * ⚠️ THE COMMISSION SCREENS CANNOT SEE THIS ON THEIR OWN. `CommissionPlansCard`
   * holds its own `milestones` array and the Commission Overview its own readiness
   * read; a milestone added in a panel floating above them leaves both stale, so
   * the plan card would keep its "no milestones yet" alert and keep `New plan`
   * disabled after the user had just fixed it.
   */
  onChanged?: () => void;
}

export function MilestoneCatalogue({ onChanged }: MilestoneCatalogueProps) {
  const [milestones, setMilestones] = useState<Milestone[]>([]);
  const [stageCounts, setStageCounts] = useState<Map<string, number>>(new Map());
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [draftName, setDraftName] = useState('');
  const [draftDescription, setDraftDescription] = useState('');
  const [adding, setAdding] = useState(false);
  const [saving, setSaving] = useState(false);

  const { toast } = useToast();
  const { currentTenant } = useTenant();

  const load = async () => {
    setLoading(true);
    // Inactive ones are shown too: a retired milestone still names history, and
    // hiding it here would make it look deleted when it is not.
    const [rows, counts] = await Promise.all([
      fetchMilestones(true),
      fetchStageCountsByMilestone(),
    ]);
    setMilestones(rows);
    setStageCounts(counts);
    setLoading(false);
  };

  useEffect(() => {
    void load();
  }, []);

  const resetDraft = () => {
    setEditingId(null);
    setAdding(false);
    setDraftName('');
    setDraftDescription('');
  };

  /** Reload, then tell the host. Both, in that order, on every successful write. */
  const settle = async () => {
    await load();
    onChanged?.();
  };

  const handleSave = async () => {
    const name = draftName.trim();
    if (!name) {
      toast({ title: 'A milestone needs a name', variant: 'destructive' });
      return;
    }

    // ⚠️ Guarded rather than asserted with `!`. This now renders inside a panel
    // that can be opened before the tenant context has resolved, which the
    // Pipelines tab could not do.
    const tenantId = currentTenant?.tenant_id;
    if (!editingId && !tenantId) {
      toast({
        title: 'Still loading this account',
        description: 'Give it a moment and try again.',
        variant: 'destructive',
      });
      return;
    }

    setSaving(true);
    const result = editingId
      ? await updateMilestone(editingId, { name, description: draftDescription.trim() || null })
      : await createMilestone(
          tenantId as string,
          name,
          draftDescription,
          // Appended rather than inserted: reordering is a separate concern and
          // guessing a position here would shuffle the list under the user.
          milestones.length,
        );
    setSaving(false);

    if (!result.ok) {
      toast({ title: 'Could not save', description: result.error, variant: 'destructive' });
      return;
    }

    toast({ title: editingId ? 'Milestone updated' : 'Milestone added' });
    resetDraft();
    void settle();
  };

  const handleRetire = async (milestone: Milestone) => {
    const result = await retireMilestone(milestone.id);
    if (!result.ok) {
      toast({ title: 'Could not retire', description: result.error, variant: 'destructive' });
      return;
    }
    toast({
      title: `${milestone.name} retired`,
      description: 'Stages keep their mapping and past dates are unaffected.',
    });
    void settle();
  };

  const handleRestore = async (milestone: Milestone) => {
    const result = await updateMilestone(milestone.id, { is_active: true });
    if (!result.ok) {
      toast({ title: 'Could not restore', description: result.error, variant: 'destructive' });
      return;
    }
    void settle();
  };

  const startEdit = (milestone: Milestone) => {
    setEditingId(milestone.id);
    setAdding(false);
    setDraftName(milestone.name);
    setDraftDescription(milestone.description ?? '');
  };

  return (
    <div className="space-y-4">
      {/*
        ⚠️ Said once, here, rather than left for someone to discover from an
        empty report. Stage history only began recording on a project's first
        MOVE, so milestones cannot be reconstructed for older work.
      */}
      <div className="rounded-md border border-border bg-muted/40 p-3 text-sm text-muted-foreground">
        Milestone dates are recorded from the moment a project moves into a mapped stage.
        Projects that reached a milestone before it was set up will show as not recorded
        rather than as missed.
      </div>

      {loading ? (
        <p className="text-sm text-muted-foreground">Loading milestones...</p>
      ) : (
        <div className="space-y-2">
          {milestones.length === 0 && !adding && (
            <p className="text-sm text-muted-foreground">
              No milestones yet. Add one, then map a stage to it.
            </p>
          )}

          {milestones.map((milestone) => {
            const mapped = stageCounts.get(milestone.id) ?? 0;
            const isEditing = editingId === milestone.id;

            return (
              <div
                key={milestone.id}
                className={`rounded-md border border-border p-3 ${milestone.is_active ? '' : 'opacity-60'}`}
              >
                {isEditing ? (
                  <div className="space-y-2">
                    <Input
                      value={draftName}
                      onChange={(e) => setDraftName(e.target.value)}
                      placeholder="Milestone name"
                    />
                    <Input
                      value={draftDescription}
                      onChange={(e) => setDraftDescription(e.target.value)}
                      placeholder="What it means (optional)"
                    />
                    <div className="flex gap-2">
                      <Button size="sm" onClick={handleSave} disabled={saving}>
                        Save
                      </Button>
                      <Button size="sm" variant="ghost" onClick={resetDraft}>
                        Cancel
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-foreground">{milestone.name}</span>
                        {!milestone.is_active && (
                          <span className="inline-flex items-center rounded-full border border-border px-2 py-0.5 text-xs text-muted-foreground">
                            Retired
                          </span>
                        )}
                      </div>
                      {milestone.description && (
                        <p className="mt-1 text-sm text-muted-foreground">{milestone.description}</p>
                      )}
                      <p className="mt-1 text-xs text-muted-foreground">
                        {/*
                          An unmapped milestone can never be reached, so it is
                          named as a gap rather than shown as a zero.
                        */}
                        {mapped === 0
                          ? 'No stage maps to this yet, so nothing can reach it.'
                          : `${mapped} ${mapped === 1 ? 'stage maps' : 'stages map'} to this.`}
                      </p>
                    </div>

                    <div className="flex shrink-0 gap-1">
                      <Button size="sm" variant="ghost" onClick={() => startEdit(milestone)}>
                        <Pencil className="h-4 w-4" />
                      </Button>
                      {milestone.is_active ? (
                        <Button
                          size="sm"
                          variant="ghost"
                          title="Retire. Stage mappings and recorded dates are kept."
                          onClick={() => handleRetire(milestone)}
                        >
                          <Archive className="h-4 w-4" />
                        </Button>
                      ) : (
                        <Button
                          size="sm"
                          variant="ghost"
                          title="Restore"
                          onClick={() => handleRestore(milestone)}
                        >
                          <RotateCcw className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                  </div>
                )}
              </div>
            );
          })}

          {adding && (
            <div className="space-y-2 rounded-md border border-dashed border-border p-3">
              <Label>New milestone</Label>
              <Input
                value={draftName}
                onChange={(e) => setDraftName(e.target.value)}
                placeholder="e.g. Install Complete"
                autoFocus
              />
              <Input
                value={draftDescription}
                onChange={(e) => setDraftDescription(e.target.value)}
                placeholder="What it means (optional)"
              />
              <div className="flex gap-2">
                <Button size="sm" onClick={handleSave} disabled={saving}>
                  Add
                </Button>
                <Button size="sm" variant="ghost" onClick={resetDraft}>
                  Cancel
                </Button>
              </div>
            </div>
          )}

          {!adding && !editingId && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                resetDraft();
                setAdding(true);
              }}
            >
              <Plus className="mr-1 h-4 w-4" />
              Add milestone
            </Button>
          )}
        </div>
      )}
    </div>
  );
}

export default MilestoneCatalogue;
